import { createId } from '../utils/ids.js';

const FLOOR_COLLECTIONS = new Set(['rooms', 'walls', 'doors', 'windows', 'stairs', 'objects', 'measurements']);

function emit(target, detail) {
  const event = typeof CustomEvent === 'function' ? new CustomEvent('change', { detail }) : new Event('change');
  if (!('detail' in event)) Object.defineProperty(event, 'detail', { value: detail });
  target.dispatchEvent(event);
}

function deepClone(value) {
  return structuredClone(value);
}

export class ProjectState extends EventTarget {
  #data;

  constructor(projectData) {
    super();
    this.#data = deepClone(projectData);
  }

  get data() {
    return this.#data;
  }

  getSnapshot() {
    return deepClone(this.#data);
  }

  replaceProject(projectData, reason = 'project-replaced') {
    this.#data = deepClone(projectData);
    this.#touch();
    emit(this, { type: reason });
  }

  findEntity(id) {
    if (this.#data.id === id) return { entity: this.#data, collection: null, floor: null, key: null, index: -1 };
    for (const [index, object] of (this.#data.objects ?? []).entries()) {
      if (object.id === id) return { entity: object, collection: this.#data.objects, floor: null, key: 'objects', index };
    }
    for (const floor of this.#data.floors ?? []) {
      if (floor.id === id) return { entity: floor, collection: this.#data.floors, floor, key: 'floors', index: this.#data.floors.indexOf(floor) };
      for (const key of FLOOR_COLLECTIONS) {
        const collection = floor[key] ?? [];
        const index = collection.findIndex((item) => item.id === id);
        if (index >= 0) return { entity: collection[index], collection, floor, key, index };
      }
    }
    return null;
  }

  patchEntity(id, patch, type = 'entity-updated') {
    const found = this.findEntity(id);
    if (!found) return false;
    Object.assign(found.entity, deepClone(patch));
    this.#touch();
    emit(this, { type, id, patch: deepClone(patch), floorId: found.floor?.id ?? null });
    return true;
  }

  patchProject(patch) {
    Object.assign(this.#data, deepClone(patch));
    this.#touch();
    emit(this, { type: 'project-settings', patch: deepClone(patch) });
  }

  addObject(object, floorId = null) {
    const clone = deepClone(object);
    const floor = floorId ? this.#data.floors.find((item) => item.id === floorId) : null;
    const collection = floor ? floor.objects : this.#data.objects;
    if (this.findEntity(clone.id)) clone.id = createId(clone.catalogId ?? 'object');
    clone.floorId = floor?.id ?? null;
    collection.push(clone);
    this.#touch();
    emit(this, { type: 'object-added', id: clone.id, floorId: floor?.id ?? null });
    return clone;
  }

  insertEntity(floorId, key, entity, index = null) {
    if (!entity || typeof entity !== 'object' || !entity.id) return null;
    const floor = floorId ? this.#data.floors.find((item) => item.id === floorId) : null;
    if (floorId && !floor) return null;
    if (floor && !FLOOR_COLLECTIONS.has(key)) return null;
    if (!floor && key !== 'objects') return null;
    const collection = floor ? (floor[key] ??= []) : this.#data.objects;
    const clone = deepClone(entity);
    if (this.findEntity(clone.id)) return this.findEntity(clone.id)?.entity ?? null;
    clone.floorId = floor?.id ?? clone.floorId ?? null;
    if (floor && ['doors', 'windows'].includes(key) && clone.wallId) {
      const wall = floor.walls.find((item) => item.id === clone.wallId);
      if (wall) {
        wall.openings ??= [];
        if (!wall.openings.some((item) => item.id === clone.id)) {
          wall.openings.push({
            id: clone.id,
            type: clone.type,
            position: clone.wallPosition ?? 0.5,
            width: clone.width,
            height: clone.height,
            sillHeight: clone.sillHeight ?? 0
          });
        }
      }
    }
    const safeIndex = Number.isInteger(index) ? Math.max(0, Math.min(index, collection.length)) : collection.length;
    collection.splice(safeIndex, 0, clone);
    this.#touch();
    emit(this, { type: `${key.replace(/s$/, '')}-added`, id: clone.id, floorId: floor?.id ?? null, key });
    return clone;
  }

  addWall(wall, floorId) {
    return this.insertEntity(floorId, 'walls', wall);
  }

  addMeasurement(measurement, floorId) {
    return this.insertEntity(floorId, 'measurements', measurement);
  }

  replaceFloorCollection(floorId, key, items, type = 'collection-replaced') {
    const floor = this.#data.floors.find((item) => item.id === floorId);
    if (!floor || !FLOOR_COLLECTIONS.has(key) || !Array.isArray(items)) return false;
    floor[key] = deepClone(items).map((item) => ({ ...item, floorId: floor.id }));
    this.#touch();
    emit(this, { type, floorId: floor.id, key, count: floor[key].length });
    return true;
  }

  removeEntity(id) {
    const found = this.findEntity(id);
    if (!found?.collection) return null;
    const index = found.collection.indexOf(found.entity);
    if (index < 0) return null;
    if (found.floor && ['doors', 'windows'].includes(found.key) && found.entity.wallId) {
      const wall = found.floor.walls.find((item) => item.id === found.entity.wallId);
      if (wall?.openings) wall.openings = wall.openings.filter((item) => item.id !== id);
    }
    const [removed] = found.collection.splice(index, 1);
    this.#touch();
    emit(this, { type: `${(found.key ?? 'entity').replace(/s$/, '')}-removed`, id, floorId: found.floor?.id ?? null, key: found.key });
    return deepClone(removed);
  }

  setFloorVisibility(floorId, visible) {
    return this.patchEntity(floorId, { visible: Boolean(visible) }, 'floor-visibility');
  }

  duplicateFloor(floorId) {
    const source = this.#data.floors.find((floor) => floor.id === floorId);
    if (!source) return null;
    const floor = deepClone(source);
    const nextLevel = Math.max(...this.#data.floors.map((item) => item.level)) + 1;
    const sourceBaseY = source.baseY ?? source.level * source.height;
    const nextBaseY = Math.max(...this.#data.floors.map((item) => (item.baseY ?? item.level * item.height) + item.height));
    const yDelta = nextBaseY - sourceBaseY;
    floor.id = createId('floor');
    floor.level = nextLevel;
    floor.baseY = nextBaseY;
    floor.name = `Floor ${nextLevel + 1}`;
    const remap = (collection, type) => collection.map((item) => ({
      ...item,
      id: createId(type),
      floorId: floor.id,
      baseY: item.baseY !== undefined ? item.baseY + yDelta : item.baseY,
      position: item.position ? [item.position[0], item.position[1] + yDelta, item.position[2]] : item.position
    }));
    for (const key of FLOOR_COLLECTIONS) floor[key] = remap(floor[key] ?? [], key.replace(/s$/, ''));
    this.#data.floors.push(floor);
    this.#touch();
    emit(this, { type: 'floor-added', id: floor.id });
    return deepClone(floor);
  }

  addFloor() {
    const source = this.#data.floors.at(-1);
    return source ? this.duplicateFloor(source.id) : null;
  }

  insertFloor(floor) {
    if (!floor || this.#data.floors.some((item) => item.id === floor.id)) return null;
    const clone = deepClone(floor);
    this.#data.floors.push(clone);
    this.#data.floors.sort((a, b) => a.level - b.level);
    this.#touch();
    emit(this, { type: 'floor-added', id: clone.id });
    return clone;
  }

  deleteFloor(floorId) {
    if (this.#data.floors.length <= 1) return null;
    const index = this.#data.floors.findIndex((floor) => floor.id === floorId);
    if (index < 0) return null;
    const [removed] = this.#data.floors.splice(index, 1);
    this.#touch();
    emit(this, { type: 'floor-removed', id: floorId });
    return deepClone(removed);
  }

  #touch() {
    this.#data.updatedAt = new Date().toISOString();
  }
}
